import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, ArrowRight, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || 'https://bissa-backend-s6sofqilwa-uc.a.run.app';

const BetaSignup = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    businessType: '',
    businessDescription: '',
    currentCrmTool: '',
    biggestChallenge: '',
    contactVolume: '',
    referralSource: '',
    additionalComments: '',
    emailConsent: false
  });

  const totalSteps = 3;
  const progress = (currentStep / totalSteps) * 100;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};
    
    if (step === 1) {
      if (!formData.name.trim()) newErrors.name = 'Name is required';
      if (!formData.email.trim()) {
        newErrors.email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Please enter a valid email address';
      }
      if (!formData.businessType) newErrors.businessType = 'Business type is required';
    }
    
    if (step === 2) {
      if (!formData.currentCrmTool) newErrors.currentCrmTool = 'Please select your current CRM tool';
      if (!formData.biggestChallenge) newErrors.biggestChallenge = 'Please select your biggest challenge';
      if (!formData.contactVolume) newErrors.contactVolume = 'Please select your contact volume';
    }
    
    if (step === 3) {
      if (!formData.emailConsent) newErrors.emailConsent = 'Email consent is required to proceed';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, totalSteps));
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateStep(currentStep)) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/api/beta/signup`, formData);
      
      if (response.data.success) {
        // Redirect to thank you page with email in query params
        navigate(`/beta/thank-you?email=${encodeURIComponent(formData.email)}`);
      }
    } catch (error) {
      console.error('Error submitting beta signup:', error);
      setErrors({
        submit: error.response?.data?.detail || 'Failed to submit. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-bissa-blue mb-2">Tell Us About Yourself</h2>
        <p className="text-gray-600">Help us understand who you are and what you do.</p>
      </div>
      
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
          Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-bissa-blue focus:border-transparent ${
            errors.name ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder="Your full name"
        />
        {errors.name && <p className="mt-1 text-sm text-red-500">{errors.name}</p>}
      </div>

      <div>
        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
          Email <span className="text-red-500">*</span>
        </label>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-bissa-blue focus:border-transparent ${
            errors.email ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder="your@email.com"
        />
        {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email}</p>}
      </div>

      <div>
        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
          Phone (Optional)
        </label>
        <input
          type="tel"
          id="phone"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bissa-blue focus:border-transparent"
          placeholder="+1 (555) 123-4567"
        />
      </div>

      <div>
        <label htmlFor="businessType" className="block text-sm font-medium text-gray-700 mb-2">
          Business Type <span className="text-red-500">*</span>
        </label>
        <select
          id="businessType"
          name="businessType"
          value={formData.businessType}
          onChange={handleChange}
          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-bissa-blue focus:border-transparent ${
            errors.businessType ? 'border-red-500' : 'border-gray-300'
          }`}
        >
          <option value="">Select your business type</option>
          <option value="Small business">Small business</option>
          <option value="Consultant">Consultant</option>
          <option value="Sales">Sales</option>
          <option value="Entrepreneur">Entrepreneur</option>
          <option value="Agency">Agency</option>
          <option value="Real Estate">Real Estate</option>
          <option value="Other">Other</option>
        </select>
        {errors.businessType && <p className="mt-1 text-sm text-red-500">{errors.businessType}</p>}
      </div>

      <div>
        <label htmlFor="businessDescription" className="block text-sm font-medium text-gray-700 mb-2">
          Business Description
        </label>
        <textarea
          id="businessDescription"
          name="businessDescription"
          value={formData.businessDescription}
          onChange={handleChange}
          rows={3}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bissa-blue focus:border-transparent"
          placeholder="Tell us briefly about your business..."
        />
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-bissa-blue mb-2">Your Current Setup</h2>
        <p className="text-gray-600">Help us understand your current workflow and challenges.</p>
      </div>

      <div>
        <label htmlFor="currentCrmTool" className="block text-sm font-medium text-gray-700 mb-2">
          Current CRM Tool <span className="text-red-500">*</span>
        </label>
        <select
          id="currentCrmTool"
          name="currentCrmTool"
          value={formData.currentCrmTool}
          onChange={handleChange}
          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-bissa-blue focus:border-transparent ${
            errors.currentCrmTool ? 'border-red-500' : 'border-gray-300'
          }`}
        >
          <option value="">Select your current tool</option>
          <option value="Spreadsheets">Spreadsheets</option>
          <option value="Basic CRM">Basic CRM</option>
          <option value="Notes/Paper">Notes/Paper</option>
          <option value="Email only">Email only</option>
          <option value="Nothing">Nothing</option>
          <option value="Other">Other</option>
        </select>
        {errors.currentCrmTool && <p className="mt-1 text-sm text-red-500">{errors.currentCrmTool}</p>}
      </div>

      <div>
        <label htmlFor="biggestChallenge" className="block text-sm font-medium text-gray-700 mb-2">
          Biggest Challenge <span className="text-red-500">*</span>
        </label>
        <select
          id="biggestChallenge"
          name="biggestChallenge"
          value={formData.biggestChallenge}
          onChange={handleChange}
          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-bissa-blue focus:border-transparent ${
            errors.biggestChallenge ? 'border-red-500' : 'border-gray-300'
          }`}
        >
          <option value="">Select your biggest challenge</option>
          <option value="Follow-ups">Follow-ups</option>
          <option value="Organization">Organization</option>
          <option value="Time">Time</option>
          <option value="Prioritization">Prioritization</option>
          <option value="Scaling">Scaling</option>
          <option value="All">All of the above</option>
        </select>
        {errors.biggestChallenge && <p className="mt-1 text-sm text-red-500">{errors.biggestChallenge}</p>}
      </div>

      <div>
        <label htmlFor="contactVolume" className="block text-sm font-medium text-gray-700 mb-2">
          Contact Volume <span className="text-red-500">*</span>
        </label>
        <select
          id="contactVolume"
          name="contactVolume"
          value={formData.contactVolume}
          onChange={handleChange}
          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-bissa-blue focus:border-transparent ${
            errors.contactVolume ? 'border-red-500' : 'border-gray-300'
          }`}
        >
          <option value="">Select your contact volume</option>
          <option value="<100">&lt;100 contacts</option>
          <option value="100-500">100-500 contacts</option>
          <option value="500-1000">500-1,000 contacts</option>
          <option value="1000-5000">1,000-5,000 contacts</option>
          <option value="5000+">5,000+ contacts</option>
        </select>
        {errors.contactVolume && <p className="mt-1 text-sm text-red-500">{errors.contactVolume}</p>}
      </div>

      <div>
        <label htmlFor="referralSource" className="block text-sm font-medium text-gray-700 mb-2">
          How did you hear about us?
        </label>
        <select
          id="referralSource"
          name="referralSource"
          value={formData.referralSource}
          onChange={handleChange}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bissa-blue focus:border-transparent"
        >
          <option value="">Select referral source</option>
          <option value="Facebook Bissa group">Facebook Bissa group</option>
          <option value="Facebook entrepreneur group">Facebook entrepreneur group</option>
          <option value="LinkedIn">LinkedIn</option>
          <option value="Twitter">Twitter</option>
          <option value="Referral">Referral</option>
          <option value="Other">Other</option>
        </select>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-bissa-blue mb-2">Almost There!</h2>
        <p className="text-gray-600">Any additional comments or questions?</p>
      </div>

      <div>
        <label htmlFor="additionalComments" className="block text-sm font-medium text-gray-700 mb-2">
          Additional Comments
        </label>
        <textarea
          id="additionalComments"
          name="additionalComments"
          value={formData.additionalComments}
          onChange={handleChange}
          rows={5}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bissa-blue focus:border-transparent"
          placeholder="Tell us anything else you'd like us to know..."
        />
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <input
            type="checkbox"
            id="emailConsent"
            name="emailConsent"
            checked={formData.emailConsent}
            onChange={handleChange}
            className="mt-1 w-4 h-4 text-bissa-blue border-gray-300 rounded focus:ring-bissa-blue"
          />
          <label htmlFor="emailConsent" className="text-sm text-gray-700">
            <span className="text-red-500">*</span> I consent to receive emails about Bissa.ai beta testing updates, 
            product news, and invitations. You can unsubscribe at any time.
          </label>
        </div>
        {errors.emailConsent && <p className="mt-2 text-sm text-red-500">{errors.emailConsent}</p>}
      </div>

      {errors.submit && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-600">{errors.submit}</p>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      {/* Header */}
      <nav className="bg-white/95 backdrop-blur-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 bg-gradient-to-br from-bissa-blue to-bissa-blue-700 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-xl">B</span>
            </div>
            <h1 className="text-2xl font-bold text-bissa-blue">Bissa</h1>
          </div>
        </div>
      </nav>

      <div className="max-w-2xl mx-auto px-6 py-12">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">
              Step {currentStep} of {totalSteps}
            </span>
            <span className="text-sm font-medium text-gray-600">
              {Math.round(progress)}%
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-gradient-to-r from-bissa-blue to-bissa-blue-700 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
          <form onSubmit={handleSubmit}>
            {/* Step Content */}
            <div className="min-h-[400px]">
              {currentStep === 1 && renderStep1()}
              {currentStep === 2 && renderStep2()}
              {currentStep === 3 && renderStep3()}
            </div>

            {/* Navigation Buttons */}
            <div className="flex items-center justify-between mt-8 pt-8 border-t border-gray-200">
              <button
                type="button"
                onClick={handleBack}
                disabled={currentStep === 1}
                className={`px-6 py-3 rounded-lg font-medium transition-all ${
                  currentStep === 1
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                Back
              </button>

              {currentStep < totalSteps ? (
                <button
                  type="button"
                  onClick={handleNext}
                  className="px-6 py-3 bg-bissa-blue text-white rounded-lg hover:bg-bissa-blue-700 transition-all font-medium inline-flex items-center gap-2"
                >
                  Next
                  <ArrowRight className="w-4 h-4" />
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-8 py-3 bg-bissa-gold text-bissa-blue rounded-lg hover:bg-yellow-400 transition-all font-semibold inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      Submit Application
                      <CheckCircle className="w-4 h-4" />
                    </>
                  )}
                </button>
              )}
            </div>
          </form>
        </div>

        {/* Footer Note */}
        <p className="text-center text-sm text-gray-500 mt-8">
          Your information is secure and will only be used for beta testing purposes.
        </p>
      </div>
    </div>
  );
};

export default BetaSignup;

